import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles,
  Download,
  FileDown,
  Printer,
  Save,
  Loader2,
  RefreshCw,
  LogIn,
  Lock,
} from "lucide-react";

import HeroHeader from "@/components/HeroHeader";
import Uploader from "@/components/Uploader";
import QuestionCard from "@/components/QuestionCard";
import SavedSetsPanel from "@/components/SavedSetsPanel";

import {
  generateQuestions,
  listSets,
  saveSet,
  deleteSet,
  downloadPdf,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STORAGE_KEY = "hikari.session.questions.v1";

function safeRead() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
function safeWrite(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

export default function HikariStudy() {
  const { isAuthed, user, loading: authLoading, startLogin } = useAuth();

  const [files, setFiles] = useState([]);
  const [chapterNote, setChapterNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [activeSetName, setActiveSetName] = useState("");
  const [pagesProcessed, setPagesProcessed] = useState(0);

  const [sets, setSets] = useState([]);
  const [setsLoading, setSetsLoading] = useState(false);

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);

  // Restore session state
  useEffect(() => {
    const cached = safeRead();
    if (cached?.questions?.length) {
      setQuestions(cached.questions);
      setChapterNote(cached.chapterNote || "");
      setActiveSetName(cached.activeSetName || "");
      setPagesProcessed(cached.pagesProcessed || 0);
    }
  }, []);

  // Persist session state
  useEffect(() => {
    if (questions.length) {
      safeWrite({ questions, chapterNote, activeSetName, pagesProcessed });
    }
  }, [questions, chapterNote, activeSetName, pagesProcessed]);

  const refreshSets = useCallback(async () => {
    setSetsLoading(true);
    try {
      const data = await listSets();
      setSets(data);
    } catch (e) {
      toast.error("Could not load saved sets");
    } finally {
      setSetsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSets();
  }, [refreshSets, isAuthed]);

  const canGenerate = !loading && files.length > 0;

  const handleGenerate = useCallback(async () => {
    if (!canGenerate) return;
    setLoading(true);
    setActiveSetName("");
    try {
      const data = await generateQuestions({ chapterNote, files });
      setQuestions(data.questions || []);
      setPagesProcessed(data.pagesProcessed || files.length);
      toast.success(
        `Generated ${data.questions?.length || 0} questions from ${
          data.pagesProcessed
        } page(s)`
      );
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "Generation failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [canGenerate, chapterNote, files]);

  const handleDownload = useCallback(async () => {
    const baseName =
      (activeSetName || "hikari-study-set")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "") || "hikari-study-set";
    const filename = `${baseName}.json`;

    const payload = {
      name: activeSetName || "Hikari Study Set",
      chapterNote,
      generatedAt: new Date().toISOString(),
      questions,
    };
    const json = JSON.stringify(payload, null, 2);

    // Preferred: File System Access API → user picks the save location.
    if (typeof window !== "undefined" && window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [
            {
              description: "Hikari Study Set (JSON)",
              accept: { "application/json": [".json"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(json);
        await writable.close();
        toast.success(`Saved as ${handle.name}`);
        return;
      } catch (err) {
        // AbortError = user cancelled, stay silent. Anything else falls back.
        if (err && err.name === "AbortError") return;
      }
    }

    // Fallback: classic <a download> click.
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  }, [activeSetName, chapterNote, questions]);

  const handleDownloadPdf = useCallback(async () => {
    if (!questions.length) return;
    try {
      const { blob, filename } = await downloadPdf({
        name: activeSetName || "Hikari Study Set",
        chapterNote,
        questions,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "PDF download failed";
      toast.error(msg);
    }
  }, [activeSetName, chapterNote, questions]);


  const handlePrint = useCallback(async () => {
    if (!questions.length) return;

    let blob;
    try {
      const result = await downloadPdf({
        name: activeSetName || "Hikari Study Set",
        chapterNote,
        questions,
      });
      blob = result.blob;
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Could not prepare the printable version";
      toast.error(msg);
      return;
    }

    const url = URL.createObjectURL(blob);

    const ua = (navigator.userAgent || "").toLowerCase();
    const isiOS =
      /iphone|ipad|ipod/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    // iOS Safari clips iframe-based prints to a single page. Open the PDF in
    // a new tab — Safari's native PDF viewer renders every page and exposes
    // the Share → Print sheet, which respects the full page count.
    if (isiOS) {
      const w = window.open(url, "_blank");
      if (!w) {
        // Pop-up blocked — surface a manual link.
        toast.info(
          "Tap Download PDF, then use Share → Print from the PDF viewer."
        );
      } else {
        toast.info("Tap Share → Print in the PDF viewer to print all pages.");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return;
    }

    // Desktop: a hidden iframe with the PDF loads the browser's PDF viewer
    // and contentWindow.print() handles all pages correctly.
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.opacity = "0";
    iframe.src = url;

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      setTimeout(() => {
        URL.revokeObjectURL(url);
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 1500);
    };

    iframe.onload = () => {
      try {
        const w = iframe.contentWindow;
        if (!w) throw new Error("no contentWindow");
        w.focus();
        w.addEventListener && w.addEventListener("afterprint", cleanup);
        setTimeout(() => {
          try {
            w.print();
          } catch {
            window.open(url, "_blank");
            toast.info("Opened the PDF — use your browser's Print button.");
            cleanup();
          }
        }, 250);
        setTimeout(cleanup, 5 * 60 * 1000);
      } catch {
        window.open(url, "_blank");
        toast.info("Opened the PDF — use your browser's Print button.");
        cleanup();
      }
    };

    document.body.appendChild(iframe);
  }, [questions, activeSetName, chapterNote]);

  const handleOpenSaveDialog = () => {
    if (!isAuthed) {
      toast.error("Sign in to save your sets to your profile.");
      startLogin();
      return;
    }
    setSaveName(activeSetName || chapterNote || "");
    setShowSaveDialog(true);
  };

  const handleSave = useCallback(async () => {
    const name = saveName.trim();
    if (!name) {
      toast.error("Please name this set");
      return;
    }
    setSaving(true);
    try {
      const created = await saveSet({ name, chapterNote, questions });
      toast.success("Saved");
      setActiveSetName(created.name);
      setShowSaveDialog(false);
      setSaveName("");
      refreshSets();
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [saveName, chapterNote, questions, refreshSets]);

  const handleOpenSet = useCallback((s) => {
    setQuestions(s.questions || []);
    setChapterNote(s.chapterNote || "");
    setActiveSetName(s.name);
    setPagesProcessed(0);
    setFiles([]);
    toast.success(`Loaded "${s.name}"`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleDeleteSet = useCallback(
    async (id) => {
      try {
        await deleteSet(id);
        toast.success("Deleted");
        refreshSets();
      } catch (e) {
        toast.error("Could not delete set");
      }
    },
    [refreshSets]
  );

  const hasResults = questions.length > 0;
  const definitionItems = useMemo(
    () => questions.filter((q) => q.type === "definition"),
    [questions]
  );
  const questionItems = useMemo(
    () => questions.filter((q) => q.type !== "definition"),
    [questions]
  );

  return (
    <div className="min-h-screen">
      <HeroHeader savedCount={sets.length} />

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-10">
        {/* Hero strap */}
        <section className="mb-8 lg:mb-10" data-testid="hero-section">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <span
                className="hk-chip hk-chip-accent"
                data-testid="hero-eyebrow"
              >
                <Sparkles size={11} />{" "}
                {!authLoading && isAuthed
                  ? `Welcome back, ${
                      (user?.name || user?.email || "Student").split(" ")[0]
                    }`
                  : "Socratic Companion"}
              </span>
              <h1
                className="font-display mt-4"
                style={{
                  fontWeight: 900,
                  letterSpacing: "-0.01em",
                  color: "#E8F0F7",
                  lineHeight: 1.05,
                  fontSize: "clamp(40px, 6.4vw, 72px)",
                }}
                data-testid="hero-title"
              >
                Turn any reading into a{" "}
                <span style={{ color: "#00BFA5" }}>study set</span>.
              </h1>
              <p
                className="mt-4"
                style={{ color: "#7A9BB5", fontSize: 17, lineHeight: 1.5 }}
                data-testid="hero-sub"
              >
                Drop in your textbook pages — JPG, PNG, or PDF — and Hikari will
                draft Socratic prompts, key definitions, and recall checks for
                students across any domain.
              </p>
              {!authLoading && !isAuthed && (
                <div
                  className="mt-5 flex items-center gap-3 flex-wrap"
                  data-testid="welcome-cta"
                >
                  <button
                    type="button"
                    onClick={startLogin}
                    className="hk-btn-accent px-4 py-2 text-sm flex items-center gap-1.5"
                    data-testid="welcome-sign-in-btn"
                  >
                    <LogIn size={13} /> Sign in to save your work
                  </button>
                  <span
                    className="font-mono"
                    style={{
                      color: "#7A9BB5",
                      fontSize: 12,
                      letterSpacing: "0.06em",
                    }}
                  >
                    or try it instantly without an account →
                  </span>
                </div>
              )}

            </div>
            <div className="hidden lg:flex gap-2">
              <span className="hk-chip">Claude · Sonnet 4.5</span>
              <span className="hk-chip">JSON · structured</span>
              <span className="hk-chip">Print · Export · Save</span>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* LEFT: workflow */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <section
              className="hk-card-strong p-6 no-print"
              data-testid="generate-card"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div
                    className="font-display"
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: "#E8F0F7",
                    }}
                  >
                    1 · Upload your material
                  </div>
                  <div style={{ color: "#7A9BB5", fontSize: 13, marginTop: 2 }}>
                    Up to 10 images or a single PDF (multi-page treated as one
                    reading).
                  </div>
                </div>
              </div>

              <Uploader files={files} setFiles={setFiles} disabled={loading} />

              <div className="mt-6">
                <label
                  className="font-mono block mb-2"
                  style={{
                    color: "#7A9BB5",
                    fontSize: 11,
                    letterSpacing: "0.12em",
                  }}
                  htmlFor="hk-chapter-input"
                >
                  2 · CHAPTER LABEL (OPTIONAL)
                </label>
                <input
                  id="hk-chapter-input"
                  type="text"
                  placeholder='e.g. "Chapter 3 — Current Liabilities"'
                  value={chapterNote}
                  onChange={(e) => setChapterNote(e.target.value)}
                  disabled={loading}
                  className="w-full px-4 py-3 rounded-lg outline-none font-body"
                  style={{
                    background: "rgba(13,35,64,0.6)",
                    border: "1px solid rgba(232,240,247,0.10)",
                    color: "#E8F0F7",
                    fontSize: 15,
                  }}
                  data-testid="chapter-input"
                />
              </div>

              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                  className="hk-btn-accent px-6 py-3 flex items-center gap-2"
                  data-testid="generate-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Generating…</span>
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      <span>Generate Socratic questions</span>
                    </>
                  )}
                </button>
                {files.length > 0 && !loading && (
                  <button
                    type="button"
                    onClick={() => setFiles([])}
                    className="hk-btn-ghost px-4 py-2 text-sm"
                    data-testid="clear-files-btn"
                  >
                    Clear
                  </button>
                )}
              </div>
            </section>

            {/* Results */}
            <section data-testid="results-section">
              {loading && (
                <div
                  className="hk-card p-8 text-center"
                  data-testid="results-loading"
                >
                  <Loader2
                    size={26}
                    className="animate-spin mx-auto mb-3"
                    color="#00BFA5"
                  />
                  <div
                    className="font-display hk-pulse"
                    style={{ color: "#E8F0F7", fontSize: 20 }}
                  >
                    Reading your pages and writing Socratic prompts…
                  </div>
                  <div
                    className="mt-1"
                    style={{ color: "#7A9BB5", fontSize: 13 }}
                  >
                    This usually takes 15–40 seconds.
                  </div>
                </div>
              )}

              {!loading && !hasResults && (
                <div
                  className="hk-card p-10 text-center"
                  data-testid="results-empty"
                >
                  <div
                    className="font-display"
                    style={{ color: "#E8F0F7", fontSize: 22, fontWeight: 700 }}
                  >
                    Your study set will appear here.
                  </div>
                  <div
                    className="mt-2"
                    style={{ color: "#7A9BB5", fontSize: 14 }}
                  >
                    Upload pages and hit{" "}
                    <span style={{ color: "#00BFA5" }}>Generate</span> to begin.
                  </div>
                </div>
              )}

              {!loading && hasResults && (
                <div className="flex flex-col gap-4">
                  <header
                    className="flex items-center justify-between flex-wrap gap-3 no-print"
                    data-testid="results-toolbar"
                  >
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="hk-chip hk-chip-accent">
                          {definitionItems.length} definitions
                        </span>
                        <span className="hk-chip hk-chip-info">
                          {questionItems.length} questions
                        </span>
                        {pagesProcessed > 0 && (
                          <span className="hk-chip">
                            {pagesProcessed} page(s) read
                          </span>
                        )}
                        {activeSetName && (
                          <span className="hk-chip">{activeSetName}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={handleOpenSaveDialog}
                        className="hk-btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5"
                        data-testid="save-set-btn"
                        title={
                          isAuthed ? "Save this set" : "Sign in to save to your profile"
                        }
                      >
                        {isAuthed ? <Save size={13} /> : <Lock size={13} />}
                        Save Set
                      </button>
                      <button
                        type="button"
                        onClick={handleDownload}
                        className="hk-btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5"
                        data-testid="download-btn"
                      >
                        <Download size={13} /> Download JSON
                      </button>
                      <button
                        type="button"
                        onClick={handleDownloadPdf}
                        className="hk-btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5"
                        data-testid="download-pdf-btn"
                      >
                        <FileDown size={13} /> Download PDF
                      </button>
                      <button
                        type="button"
                        onClick={handlePrint}
                        className="hk-btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5"
                        data-testid="print-btn"
                      >
                        <Printer size={13} /> Print
                      </button>
                    </div>
                  </header>

                  {definitionItems.length > 0 && (
                    <section data-testid="definitions-section">
                      <div className="flex items-baseline justify-between mb-3">
                        <h2
                          className="font-display"
                          style={{
                            color: "#E8F0F7",
                            fontWeight: 800,
                            fontSize: 26,
                            letterSpacing: "-0.005em",
                          }}
                          data-testid="definitions-heading"
                        >
                          Definitions
                        </h2>
                        <span
                          className="font-mono"
                          style={{
                            color: "#7A9BB5",
                            fontSize: 11,
                            letterSpacing: "0.12em",
                          }}
                        >
                          {definitionItems.length} ITEMS
                        </span>
                      </div>
                      <div
                        className="grid grid-cols-1 md:grid-cols-2 gap-4"
                        data-testid="definitions-grid"
                      >
                        {definitionItems.map((q, i) => (
                          <QuestionCard
                            key={`def-${i}`}
                            item={q}
                            index={i}
                            testIdPrefix="definition"
                          />
                        ))}
                      </div>
                    </section>
                  )}

                  {questionItems.length > 0 && (
                    <section data-testid="questions-section">
                      <div className="flex items-baseline justify-between mb-3 mt-2">
                        <h2
                          className="font-display"
                          style={{
                            color: "#E8F0F7",
                            fontWeight: 800,
                            fontSize: 26,
                            letterSpacing: "-0.005em",
                          }}
                          data-testid="questions-heading"
                        >
                          Questions
                        </h2>
                        <span
                          className="font-mono"
                          style={{
                            color: "#7A9BB5",
                            fontSize: 11,
                            letterSpacing: "0.12em",
                          }}
                        >
                          {questionItems.length} ITEMS
                        </span>
                      </div>
                      <div
                        className="grid grid-cols-1 md:grid-cols-2 gap-4"
                        data-testid="questions-grid"
                      >
                        {questionItems.map((q, i) => (
                          <QuestionCard
                            key={`q-${i}`}
                            item={q}
                            index={i}
                            testIdPrefix="question"
                          />
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </section>
          </div>

          {/* RIGHT: saved sets */}
          <div className="no-print">
            <div className="lg:sticky lg:top-6 flex flex-col gap-4">
              <SavedSetsPanel
                sets={sets}
                loading={setsLoading}
                onOpen={handleOpenSet}
                onDelete={handleDeleteSet}
              />
              <button
                type="button"
                onClick={refreshSets}
                className="hk-btn-ghost px-3 py-1.5 text-xs flex items-center justify-center gap-1.5 self-end"
                data-testid="refresh-sets-btn"
              >
                <RefreshCw size={11} /> Refresh
              </button>
            </div>
          </div>
        </div>

        <footer
          className="mt-14 pb-10 text-center no-print"
          data-testid="footer"
        >
          <div
            className="font-mono"
            style={{ color: "#7A9BB5", fontSize: 11, letterSpacing: "0.18em" }}
          >
            HIKARI STUDY · PRECISE · TECHNICAL · TRUSTWORTHY
          </div>
        </footer>
      </main>

      {/* Save Set dialog */}
      {showSaveDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 no-print"
          style={{ background: "rgba(6,15,26,0.78)", backdropFilter: "blur(6px)" }}
          onClick={() => !saving && setShowSaveDialog(false)}
          data-testid="save-dialog-overlay"
        >
          <div
            className="hk-card-strong p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="save-dialog"
          >
            <div
              className="font-display mb-1"
              style={{ color: "#E8F0F7", fontSize: 22, fontWeight: 700 }}
            >
              Name this set
            </div>
            <div style={{ color: "#7A9BB5", fontSize: 13 }} className="mb-4">
              It will be stored so you can return to it later.
            </div>
            <input
              autoFocus
              type="text"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="e.g. Ch.3 Liabilities Drill"
              className="w-full px-4 py-3 rounded-lg outline-none mb-4"
              style={{
                background: "rgba(13,35,64,0.6)",
                border: "1px solid rgba(232,240,247,0.10)",
                color: "#E8F0F7",
                fontSize: 15,
              }}
              data-testid="save-dialog-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
              }}
            />
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowSaveDialog(false)}
                disabled={saving}
                className="hk-btn-ghost px-4 py-2 text-sm"
                data-testid="save-dialog-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="hk-btn-accent px-4 py-2 text-sm flex items-center gap-1.5"
                data-testid="save-dialog-confirm"
              >
                {saving ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Save size={13} />
                )}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
